/**
 * Skeleton loading primitives.
 *
 * Use these instead of spinners for content-area loading states.
 * They communicate layout shape while data loads, improving perceived performance.
 *
 * Usage:
 *   <Skeleton.Card />           — Metric card placeholder
 *   <Skeleton.Row />            — List row placeholder
 *   <Skeleton.Text width="w-48" /> — Text line placeholder
 *   <Skeleton.Block height="h-32" /> — Generic block placeholder
 */

const pulseClass = 'animate-pulse bg-gray-200 rounded';

export function SkeletonText({ width = 'w-full', height = 'h-3', className = '' }) {
  return <div className={`${pulseClass} ${width} ${height} ${className}`} />;
}

export function SkeletonCard({ className = '' }) {
  return (
    <div className={`p-4 bg-white rounded-lg border border-gray-200 shadow-sm ${className}`}>
      <div className="flex justify-between items-start mb-3">
        <div className={`${pulseClass} w-8 h-8`} />
        <SkeletonText width="w-16" height="h-2" />
      </div>
      <SkeletonText width="w-16" height="h-6" className="mb-2" />
      <SkeletonText width="w-24" height="h-2.5" className="mb-1" />
      <SkeletonText width="w-32" height="h-2" />
    </div>
  );
}

export function SkeletonRow({ className = '' }) {
  return (
    <div className={`flex gap-3 items-center px-4 py-3 bg-white rounded-lg border border-gray-200 ${className}`}>
      <div className={`${pulseClass} w-10 h-10 rounded-lg flex-shrink-0`} />
      <div className="flex-1 space-y-2">
        <SkeletonText width="w-48" height="h-3" />
        <SkeletonText width="w-32" height="h-2" />
      </div>
      <SkeletonText width="w-16" height="h-5 rounded-full" />
    </div>
  );
}

export function SkeletonBlock({ height = 'h-32', className = '' }) {
  return <div className={`${pulseClass} w-full ${height} ${className}`} />;
}

/**
 * Dashboard skeleton — shows the full dashboard layout shape while loading.
 */
export function DashboardSkeleton() {
  return (
    <div className="w-full">
      <div className="mx-auto max-w-7xl">
        {/* Welcome row */}
        <div className="flex justify-between items-start mb-6">
          <div className="space-y-2">
            <SkeletonText width="w-64" height="h-6" />
            <SkeletonText width="w-80" height="h-3" />
          </div>
          <SkeletonBlock height="h-16 w-80" className="rounded-lg hidden lg:block" />
        </div>

        {/* Metric cards */}
        <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-3 md:gap-6">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>

        {/* Two-column content */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 md:gap-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <SkeletonText width="w-32" height="h-4" />
            </div>
            <div className="p-6 space-y-4">
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <SkeletonText width="w-32" height="h-4" />
            </div>
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="flex gap-3 items-center">
                  <div className={`${pulseClass} w-8 h-8 rounded-full flex-shrink-0`} />
                  <div className="flex-1 space-y-1">
                    <SkeletonText width="w-48" height="h-3" />
                    <SkeletonText width="w-24" height="h-2" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Trips list skeleton — shows analytics cards + trip rows shape.
 */
export function TripsListSkeleton() {
  return (
    <div>
      {/* Analytics cards */}
      <div className="grid grid-cols-2 gap-3 mb-4 sm:grid-cols-3 lg:grid-cols-6">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <div key={i} className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="flex gap-2 items-center mb-2">
              <div className={`${pulseClass} w-4 h-4`} />
              <SkeletonText width="w-16" height="h-2" />
            </div>
            <SkeletonText width="w-12" height="h-5" className="mb-1" />
            <SkeletonText width="w-20" height="h-2" />
          </div>
        ))}
      </div>

      {/* Search/filter bar */}
      <div className="flex gap-3 mb-4">
        <SkeletonBlock height="h-10 flex-1 max-w-xs" className="rounded-lg" />
        <SkeletonBlock height="h-10 w-64" className="rounded-lg" />
      </div>

      {/* Trip rows */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-4 border-b border-gray-200">
          <SkeletonText width="w-28" height="h-4" />
        </div>
        <div className="p-4 space-y-2">
          {[1, 2, 3, 4].map(i => (
            <SkeletonRow key={i} />
          ))}
        </div>
      </div>
    </div>
  );
}
